import { useGetDownloadSignedUrlForQuestionsByQSetId } from '@exam-room/graphql/preview';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';

import {
  precacheSignedUrls,
  isServiceWorkerActive,
  type SignedImagePayload,
  type PrecacheProgressPayload,
} from '../../../services/serviceWorker';
import type { QuestionWithOrder, Choice, CacheStatus } from '../../new-exam/types';
import { mapSignedUrlToImages, mapSignedUrlToSyntheticUrls } from '../../new-exam/utils';

export function useGetQuestionsWithImage(
  questions: QuestionWithOrder[] = [],
  qSetId?: string,
  taskId?: string,
) {
  const [parsedQuestions, setParsedQuestions] = useState<QuestionWithOrder[]>(questions);
  const [isMappingQuestionsWithImages, setIsMappingQuestionsWithImages] = useState(false);
  const [cacheStatus, setCacheStatus] = useState<CacheStatus>({
    isCaching: false,
    cachedCount: 0,
    totalCount: 0,
    isComplete: false,
  });

  const {
    data,
    loading: isLoadingSignedUrls,
    error: getSignedUrlsError,
  } = useGetDownloadSignedUrlForQuestionsByQSetId({
    variables: {
      input: {
        qSetId: qSetId as string,
        taskId: taskId as string,
      },
    },
    skip: !qSetId || !taskId,
  });

  // cspell:disable-next-line
  const handleProgress = useCallback((payload: PrecacheProgressPayload) => {
    setCacheStatus((prev) => ({
      ...prev,
      cachedCount: payload.count,
    }));
  }, []);

  useEffect(() => {
    if (data?.excGetDownloadSignedUrlForQuestionsByQSetId.signedUrls && !isLoadingSignedUrls) {
      setIsMappingQuestionsWithImages(true);
      const imgUrls: Record<string, Record<string, string>> = data
        .excGetDownloadSignedUrlForQuestionsByQSetId.signedUrls;

      const parsedWithSignedUrls = questions.map((question) => ({
        ...question,
        question: mapSignedUrlToImages(question.question, imgUrls[question.id]),
        metadata: {
          ...question.metadata,
          choices: question.metadata?.choices?.map((choice: Choice) => ({
            ...choice,
            label: mapSignedUrlToImages(choice.label, imgUrls[question.id]),
          })),
          paragraphs: mapSignedUrlToImages(
            question.metadata?.paragraphs,
            imgUrls[question.metadata.questionGroupId],
          ),
          solution: mapSignedUrlToImages(question.metadata?.solution, imgUrls[question.id]),
        },
      }));

      const imagesToCache: SignedImagePayload[] = [];
      // questionId can be both questionId and question group id, but it doesn't matter for caching as long as we use the correct mapping when replacing with synthetic URLs later
      Object.entries(imgUrls).forEach(([questionId, urlMap]) => {
        Object.entries(urlMap).forEach(([filename, signedUrl]) => {
          const imageUd = `${questionId}/${filename}`;
          imagesToCache.push({ id: imageUd, signedUrl });
        });
      });

      if (isServiceWorkerActive() && imagesToCache.length > 0) {
        // console.log('[useGetQuestionWithImage] Starting to cache', imagesToCache.length, 'images');
        setCacheStatus({
          isCaching: true,
          cachedCount: 0,
          totalCount: imagesToCache.length,
          isComplete: false,
        });

        precacheSignedUrls(imagesToCache, handleProgress)
          .then((result) => {
            // console.log('[useGetQuestionWithImage] Caching complete:', result);
            const cacheErrorMessage =
              result.failed > 0 ? `${result.failed} images failed to cache` : undefined;
            setCacheStatus({
              isCaching: false,
              cachedCount: result.count,
              totalCount: result.total,
              isComplete: result.ok && result.failed === 0,
              error: cacheErrorMessage,
            });
            const parsedWithSyntheticUrls = questions.map((question) => ({
              ...question,
              question: mapSignedUrlToSyntheticUrls(
                question.question,
                question.id,
                imgUrls[question.id],
              ),
              metadata: {
                ...question.metadata,
                paragraphs: mapSignedUrlToSyntheticUrls(
                  question.metadata?.paragraphs,
                  question.metadata.questionGroupId,
                  imgUrls[question.metadata.questionGroupId],
                ),
                choices: question.metadata?.choices?.map((choice: Choice) => ({
                  ...choice,
                  label: mapSignedUrlToSyntheticUrls(
                    choice.label,
                    question.id,
                    imgUrls[question.id],
                  ),
                })),
                solution: mapSignedUrlToSyntheticUrls(
                  question.metadata?.solution,
                  question.id,
                  imgUrls[question.id],
                ),
              },
            }));

            setParsedQuestions(parsedWithSyntheticUrls);
          })
          .catch((err) => {
            console.error('[useGetQuestionsWithImage] Failed to cache images:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to cache images';
            setCacheStatus({
              isCaching: false,
              cachedCount: 0,
              totalCount: imagesToCache.length,
              isComplete: false,
              error: errorMessage,
            });
            setParsedQuestions(parsedWithSignedUrls);
            Sentry.captureException(err, {
              tags: {
                feature: 'image-caching',
              },
              extra: {
                imagesToCache,
              },
            });
          })
          .finally(() => {
            setIsMappingQuestionsWithImages(false);
          });
      } else {
        // console.warn(
        //   '[useGetQuestionsWithImage] Service worker not supported or no images to cache',
        // );
        setParsedQuestions(parsedWithSignedUrls);
        setIsMappingQuestionsWithImages(false);
      }
    }
  }, [
    questions,
    data?.excGetDownloadSignedUrlForQuestionsByQSetId.signedUrls,
    isLoadingSignedUrls,
  ]);

  return {
    data: parsedQuestions,
    loading: isLoadingSignedUrls || isMappingQuestionsWithImages,
    cacheStatus,
    error: getSignedUrlsError,
  };
}
